import React, { useEffect, useState, useMemo, useContext } from "react";

import "workspace/components/styles/panel.css";
import "wxrecall/components/Image.css";

import calculateRatio from "workspace/components/panel-ratios";
import {
  colourCurvesForKey,
  BASE_PLAYBACK_INTERVAL,
  BASE_PLAYBACK_LAST_FRAME_INTERVAL
} from "workspace/components/Panel";
import PanelHeader from "workspace/components/panel/PanelHeader";
import { PanelImageClass } from "workspace/components/panel/PanelImage";
import PanelFooter from "workspace/components/panel/PanelFooter";

import { AppContext } from "wxrecall/components/App";

import { calculateExtraHeight } from "workspace/config";

import { colourizedImageSetId } from "workspace/reducers/image";

import {
  defaultFrame,
  defaultFrameList,
  defaultImage
} from "workspace/selectors/default";

import { getScaleForKey } from "workspace/util/temperatureScales";
import { colourizeImage } from "workspace/util/colourizer";

function urlForImage(image) {
  const url = `${window.urls.staticHost}/weather/images/${image.id}.image`;
  return url;
}

function getDimensions(compositeKey, isPanelOptionsOpen) {
  const width = 758;
  const ratio = calculateRatio(compositeKey);

  return {
    ratio,
    width,
    height: width * (1 / ratio),
    extraHeight: calculateExtraHeight([], [true], isPanelOptionsOpen)
  };
}

function Image(props) {
  // check if global blobs object exists, or create it. Use to store blob urls accross panels
  if (!window.colouredBlobs) {
    window.colouredBlobs = {};
  }

  const appContext = useContext(AppContext);
  const [zoom, setZoom] = useState(1);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [isPlayback, setPlayback] = useState(false);
  const [isPanelOptionsOpen, setIsPanelOptionsOpen] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [colourCurve, setColourCurve] = useState(0);
  const unixTimestamp = props.now.unix();
  const [temperatureDisplayEnabled, setTemperatureDisplay] = useState(false);
  const temperatureScale = useMemo(() => getScaleForKey(props.data), []);
  const temperatureDisplayAllowed = temperatureScale !== null;

  const activeFrameList = useMemo(
    () => defaultFrameList(props.now, props.data.frame_lists),
    [
      unixTimestamp,
      props.data.geography,
      props.data.subGeography,
      props.data.product,
      props.data.subProduct
    ]
  );

  const currentFrame = useMemo(
    () => defaultFrame(props.now, activeFrameList.frames),
    [unixTimestamp, activeFrameList.id]
  );

  const [index, setIndex] = useState(() =>
    activeFrameList.frames.indexOf(currentFrame)
  );
  const [timer, setTimer] = useState(null);

  const activeFrame = activeFrameList.frames[index];
  const activeImage = useMemo(
    () => defaultImage(activeFrame.images),
    [activeFrame.id]
  );

  const [toggle, setToggle] = useState(false); // a boolean to flip when the image has been colourized.
  const originalUrl = window.urls.image(activeImage.id);
  const maxIndex = activeFrameList.frames.length - 1;
  const nowIndex = useMemo(
    () => activeFrameList.frames.findIndex(f => f.id === currentFrame.id),
    [currentFrame.id]
  );

  const dimensions = useMemo(
    () => getDimensions(props.data, isPanelOptionsOpen),
    [props.data, isPanelOptionsOpen]
  );

  // All these consts may be a good candidate for useReducer..

  function togglePlayback() {
    setPlayback(!isPlayback);
  }

  function next() {
    // Determine the next image, make an XHR request for it and when
    // the data is recieved, update the index to the new value.
    // The image should be retrieved from the browser cache
    const nextIndex = (index + 1) % activeFrameList.frames.length;
    const nextFrame = activeFrameList.frames[nextIndex];
    const nextImage = defaultImage(nextFrame.images);
    const url = urlForImage(nextImage);

    props.networkManager.requests
      .fetchImage({ url })
      .then(data => setIndex(nextIndex));
  }

  function prev() {
    let prev = index - 1;
    if (prev < 0) prev = maxIndex;

    const nextFrame = activeFrameList.frames[prev];
    const nextImage = defaultImage(nextFrame.images);
    const url = urlForImage(nextImage);

    props.networkManager.requests
      .fetchImage({ url })
      .then(data => setIndex(prev));
  }

  function togglePanelOptions() {
    setIsPanelOptionsOpen(!isPanelOptionsOpen);
  }

  function updatePlaybackSpeed(panelId, speedModifier) {
    setPlaybackSpeed(speedModifier);
  }

  const curves = {
    selected: colourCurve,
    options: colourCurvesForKey(window.settings.colourCurves || {}, props.data),
    onChange: e => setColourCurve(e)
  };

  useEffect(() => {
    if (isPlayback) {
      const baseTimeout =
        index === nowIndex
          ? BASE_PLAYBACK_LAST_FRAME_INTERVAL
          : BASE_PLAYBACK_INTERVAL;
      const timeout = baseTimeout / playbackSpeed;
      if (timer) clearTimeout(timer); // We don't want more than one going at a time

      // Pre-load image and then set timeout.
      const nextFrame =
        activeFrameList.frames[(index + 1) % activeFrameList.frames.length];
      const url = urlForImage(defaultImage(nextFrame.images));
      props.networkManager.requests
        .fetchImage({ url })
        .then(data => {
          setTimer(setTimeout(next, timeout)); // store the timeout timer so we can cancel when the user stops playback
        })
        .catch(error =>
          console.error("Error while querying for data: " + error)
        );
    } else {
      clearTimeout(timer);
    }
    // We only want to advance/cancel playback if playback has toggled, speed or frame has changed
  }, [isPlayback, playbackSpeed, index]);

  useEffect(() => {
    // Cleanup the coloured blobs when the panel is removed.
    // revoke each blob and reset the global blob object
    return () => {
      const urlWindow = window.URL || window.webkitURL;
      Object.values(window.colouredBlobs).forEach(blob =>
        urlWindow.revokeObjectURL(blob)
      );
      window.colouredBlobs = {};
    };
  }, []);

  // Set the setFramecallback on each frame so that it can be used inside the FrameSelectorGroup.
  activeFrameList.frames.forEach(
    (frame, i) =>
      (frame.setFrame = () => {
        setPlayback(false);
        // Pre-load image before updating the index
        const nextFrame = activeFrameList.frames[i];
        const url = urlForImage(defaultImage(nextFrame.images));
        props.networkManager.requests.fetchImage({ url }).then(data => {
          setIndex(i);
        });
      })
  );

  function getImageUrl(imageId) {
    // get the corerct url for the image: coloured or not:
    let imgUrl;
    const colouredId = colourizedImageSetId(imageId, colourCurve);
    try {
      if (window.settings.canViewSatelliteColour && colourCurve) {
        imgUrl = window.colouredBlobs[colouredId];
        if (imgUrl === undefined) {
          colourizeImage(
            curves.options.map[colourCurve].map,
            originalUrl,
            blob => {
              window.colouredBlobs[colouredId] = blob;
              setToggle(s => !s);
            }
          );
        }
      } else {
        imgUrl = originalUrl;
      }
      return imgUrl || originalUrl;
    } catch (e) {
      return originalUrl || null;
    }
  }

  let imgUrl = useMemo(
    () => getImageUrl(activeImage.id),
    [activeImage.id, colourCurve, toggle]
  );

  let sizeStyle={
    height: dimensions.height + dimensions.extraHeight
  }
  if(!appContext.mobile){
    sizeStyle['width'] = dimensions.width;
  }

  return (
    <div
      className="wxrecall-panel panel"
      style={sizeStyle}
    >
      <PanelHeader
        panelId={props.id}
        activeImageId={activeImage.id}
        compositeKey={props.data}
        isLiveMode={false}
        playbackSpeed={{
          current: playbackSpeed,
          update: updatePlaybackSpeed
        }}
        panelOptions={{
          isOpen: isPanelOptionsOpen,
          toggle: togglePanelOptions
        }}
        colourCurve={curves}
        temperatureDisplayScale={temperatureScale}
        temperatureDisplayAllowed={temperatureDisplayAllowed}
        temperatureDisplayEnabled={temperatureDisplayEnabled}
        updateTemperatureDisplay={() =>
          setTemperatureDisplay(!temperatureDisplayEnabled)
        }
      />
      <PanelImageClass
        panelRatio={dimensions.ratio}
        panelWidth={dimensions.width}
        crop={crop}
        updateCrop={setCrop}
        hasFrameList={true}
        zoom={zoom}
        updateZoom={setZoom}
        colourCurve={curves}
        imgUrl={imgUrl}
        compositeKey={props.data}
        greyScaleUrl={originalUrl}
        temperatureDisplayAllowed={temperatureDisplayAllowed}
        temperatureDisplayEnabled={temperatureDisplayEnabled}
        temperatureDisplayScale={temperatureScale}
      />
      <PanelFooter
        frameListId={activeFrameList.id}
        frame={{
          list: activeFrameList.frames,
          active: activeFrame,
          current: currentFrame
        }}
        image={{
          active: activeImage,
          selector: false
        }}
        playback={{
          isEnabled: isPlayback,
          toggle: togglePlayback
        }}
        previousFrame={() => {
          setPlayback(false);
          prev();
        }}
        nextFrame={() => {
          setPlayback(false);
          next();
        }}
      />
    </div>
  );
}

export default Image;
